/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import {Logger} from '@app/lib/Logger';
import type {AudioWindow} from '@app/types/electron.d';
import {getElectronAPI} from '@app/utils/NativeUtils';

const logger = new Logger('AppAudioCapture');

// Matches the vendored WASAPI loopback helper (see fluxer_desktop/native/app-audio):
// raw interleaved PCM, 48 kHz, 2 channels, signed 16-bit little-endian.
const SAMPLE_RATE = 48_000;
const CHANNELS = 2;
const BYTES_PER_FRAME = CHANNELS * 2;
const SYSTEM_CAPTURE_KEY = 'system';

// 'include' = stream ONLY this process's audio (a shared window). 'exclude' = stream all system audio
// MINUS Fluxer's own process (so viewers don't hear themselves and no echo-canceller is needed).
export type AppAudioSpec = {mode: 'include'; processId: string} | {mode: 'exclude'};

// MediaStreamTrackGenerator + AudioData (mediacapture-transform / WebCodecs) are available in Electron's
// Chromium but not in the DOM lib typings, so declare the slice we use.
interface MediaStreamTrackGeneratorInit {
	kind: 'audio' | 'video';
}
declare const MediaStreamTrackGenerator:
	| {new (init: MediaStreamTrackGeneratorInit): MediaStreamTrack & {readonly writable: WritableStream<AudioData>}}
	| undefined;

export interface AppAudioCapture {
	track: MediaStreamTrack;
	stop: () => void;
}

// True when this build can capture a single application's audio (INCLUDE) — Windows desktop, 1.0.3+.
export function supportsPerAppAudioCapture(): boolean {
	const api = getElectronAPI();
	return Boolean(
		api &&
			api.platform === 'win32' &&
			typeof api.appAudioStart === 'function' &&
			typeof api.appAudioStop === 'function' &&
			typeof api.onAppAudioData === 'function' &&
			typeof MediaStreamTrackGenerator !== 'undefined',
	);
}

// True when this build can capture all system audio EXCEPT Fluxer (EXCLUDE) — Windows desktop, 1.0.4+.
// This is the capability that lets system-audio streaming drop the echo-canceller and switch live.
export function supportsSystemExcludeAudioCapture(): boolean {
	const api = getElectronAPI();
	return supportsPerAppAudioCapture() && typeof api?.appAudioStartSystem === 'function';
}

export async function listAudioWindows(): Promise<Array<AudioWindow>> {
	const api = getElectronAPI();
	if (!api?.appAudioListWindows) {
		return [];
	}
	try {
		return await api.appAudioListWindows();
	} catch (error) {
		logger.warn('Failed to list audio windows', {error});
		return [];
	}
}

// Capture audio per `spec` and expose it as a live MediaStreamTrack to publish as ScreenShareAudio.
// PCM frames arrive over IPC from the native helper and are fed into a MediaStreamTrackGenerator as
// AudioData. Returns null if capture is unavailable or the native helper refused to start.
export async function createAppAudioTrack(spec: AppAudioSpec): Promise<AppAudioCapture | null> {
	const api = getElectronAPI();
	const TrackGenerator = MediaStreamTrackGenerator;
	if (!api?.appAudioStop || !api.onAppAudioData || !TrackGenerator) {
		logger.warn('Per-app audio capture unavailable on this platform');
		return null;
	}
	const captureKey = spec.mode === 'include' ? spec.processId : SYSTEM_CAPTURE_KEY;

	const generator = new TrackGenerator({kind: 'audio'});
	const writer = generator.writable.getWriter();
	let framesWritten = 0;
	let leftover = new Uint8Array(0);
	let stopped = false;

	const unsubscribe = api.onAppAudioData((key, chunk) => {
		if (stopped || key !== captureKey) {
			return;
		}
		// stdout chunks can split mid-frame; carry the remainder so we only ever emit whole frames.
		const incoming = chunk instanceof Uint8Array ? chunk : new Uint8Array(chunk);
		let data: Uint8Array;
		if (leftover.length > 0) {
			data = new Uint8Array(leftover.length + incoming.length);
			data.set(leftover, 0);
			data.set(incoming, leftover.length);
		} else {
			data = incoming;
		}

		const usableBytes = data.length - (data.length % BYTES_PER_FRAME);
		if (usableBytes < BYTES_PER_FRAME) {
			leftover = data.slice();
			return;
		}
		leftover = data.length > usableBytes ? data.slice(usableBytes) : new Uint8Array(0);

		const numberOfFrames = usableBytes / BYTES_PER_FRAME;
		try {
			const audioData = new AudioData({
				format: 's16',
				sampleRate: SAMPLE_RATE,
				numberOfFrames,
				numberOfChannels: CHANNELS,
				timestamp: Math.round((framesWritten / SAMPLE_RATE) * 1_000_000),
				data: data.subarray(0, usableBytes),
			});
			framesWritten += numberOfFrames;
			void writer.write(audioData).catch(() => {
				// stream closed / errored — capture is stopping
			});
		} catch (error) {
			logger.warn('Failed to enqueue app audio frame', {error});
		}
	});

	let started = false;
	try {
		started =
			spec.mode === 'include'
				? Boolean(await api.appAudioStart?.(spec.processId))
				: Boolean(await api.appAudioStartSystem?.());
	} catch (error) {
		logger.error('Native audio capture failed to start', {spec, error});
	}

	if (!started) {
		unsubscribe();
		void writer.close().catch(() => {});
		generator.stop();
		return null;
	}

	logger.info('Native stream audio capture started', {mode: spec.mode, captureKey});

	const stop = () => {
		if (stopped) {
			return;
		}
		stopped = true;
		try {
			api.appAudioStop?.(captureKey);
		} catch (error) {
			logger.warn('appAudioStop threw', {captureKey, error});
		}
		unsubscribe();
		void writer.close().catch(() => {});
		generator.stop();
		logger.info('Native stream audio capture stopped', {captureKey});
	};

	return {track: generator, stop};
}
